import { Slider } from '../controls/Slider';
import { useActive } from './useActive';
import { useUi } from '../../state/ui';
import { useT, type MessageKey } from '../../i18n';
import { ASPECTS, constrainCrop, fitAspect } from '../../engine/geometry';
import type { AspectId, Settings } from '../../model/types';

// Aspect presets (§11.3) incl. the platform-named ones.
const OPTIONS: { value: string; aspect: AspectId; label: MessageKey | string }[] = [
  { value: 'free', aspect: 'free', label: 'aspectFree' },
  { value: 'original', aspect: 'original', label: 'aspectOriginal' },
  { value: '1:1', aspect: '1:1', label: '1:1' },
  { value: '4:5', aspect: '4:5', label: '4:5' },
  { value: '3:2', aspect: '3:2', label: '3:2' },
  { value: '2:3', aspect: '2:3', label: '2:3' },
  { value: '16:9', aspect: '16:9', label: '16:9' },
  { value: '9:16', aspect: '9:16', label: '9:16' },
  { value: '4:3', aspect: '4:3', label: '4:3' },
  { value: 'ig-square', aspect: '1:1', label: 'aspectIgSquare' },
  { value: 'ig-portrait', aspect: '4:5', label: 'aspectIgPortrait' },
  { value: 'story', aspect: '9:16', label: 'aspectStory' },
  { value: 'dish', aspect: '1:1', label: 'aspectDish' },
];

/** Re-derives the crop after an aspect, angle or constrain change (pure; also used by the crop tool). */
export function reframe(s: Settings, W: number, H: number, opts: { refit: boolean }) {
  const g = s.geometry;
  const a =
    g.aspect === 'original' ? W / H : g.aspect && g.aspect !== 'free' ? (ASPECTS[g.aspect] ?? null) : null;
  if (opts.refit) g.crop = a ? fitAspect(a, W, H) : { x: 0, y: 0, w: 1, h: 1 };
  if (g.constrain) g.crop = constrainCrop(g, W, H);
}

export function GeometryPanel() {
  const t = useT();
  const { settings, update, photo } = useActive();
  const tool = useUi((s) => s.tool);
  const W = photo?.width || 1,
    H = photo?.height || 1;
  const g = settings.geometry;
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
        <button
          aria-pressed={tool === 'crop'}
          data-testid="crop-tool"
          onClick={() => useUi.getState().set({ tool: tool === 'crop' ? 'none' : 'crop' })}
          style={tool === 'crop' ? { background: 'var(--accent)', color: '#fff' } : undefined}
        >
          {t('cropTool')} (R)
        </button>
        <button
          aria-pressed={tool === 'straighten'}
          onClick={() => useUi.getState().set({ tool: tool === 'straighten' ? 'none' : 'straighten' })}
          style={tool === 'straighten' ? { background: 'var(--accent)', color: '#fff' } : undefined}
        >
          {t('straighten')}
        </button>
      </div>
      {tool === 'straighten' && <div style={{ fontSize: 12, marginBottom: 6 }}>{t('straightenHint')}</div>}
      <label
        style={{
          display: 'grid',
          gridTemplateColumns: '92px 1fr',
          gap: 8,
          alignItems: 'center',
          fontSize: 13,
          color: 'var(--text)',
        }}
      >
        {t('aspect')}
        <select
          data-testid="aspect"
          value={OPTIONS.find((o) => o.aspect === (g.aspect ?? 'free'))?.value ?? 'free'}
          onChange={(e) => {
            const o = OPTIONS.find((x) => x.value === e.target.value)!;
            update((s) => {
              s.geometry.aspect = o.aspect === 'free' ? null : o.aspect;
              reframe(s, W, H, { refit: true });
            });
          }}
          style={{
            background: 'var(--bg)',
            color: 'var(--text)',
            border: '1px solid var(--line)',
            borderRadius: 3,
            padding: 2,
          }}
        >
          {OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label.includes(':') && !o.label.startsWith('aspect') ? o.label : t(o.label as MessageKey)}
            </option>
          ))}
        </select>
      </label>
      <Slider
        label="angle"
        testId="slider-angle"
        value={g.angle}
        min={-45}
        max={45}
        step={0.1}
        digits={1}
        onChange={(v, f) =>
          update((s) => {
            s.geometry.angle = v;
            reframe(s, W, H, { refit: true });
          }, f)
        }
      />
      {(['vertical', 'horizontal'] as const).map((k) => (
        <Slider
          key={k}
          label={k}
          testId={`slider-${k}`}
          value={g[k]}
          min={-100}
          max={100}
          step={1}
          onChange={(v, f) =>
            update((s) => {
              s.geometry[k] = v;
              reframe(s, W, H, { refit: true });
            }, f)
          }
        />
      ))}
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, marginTop: 4 }}>
        <input
          type="checkbox"
          checked={g.constrain}
          onChange={(e) =>
            update((s) => {
              s.geometry.constrain = e.target.checked;
              reframe(s, W, H, { refit: true });
            })
          }
        />
        {t('constrain')}
      </label>
      <button
        style={{ marginTop: 6 }}
        onClick={() =>
          update((s) => {
            s.geometry = {
              crop: { x: 0, y: 0, w: 1, h: 1 },
              aspect: null,
              angle: 0,
              vertical: 0,
              horizontal: 0,
              constrain: true,
            };
          })
        }
      >
        {t('reset')}
      </button>
    </div>
  );
}
